import cn from 'classnames';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  CustomerSubscriptionSource,
  CustomerSubscriptionStatus,
} from '@lambda/apis/src/customer/enum';
import { CtaButton } from '@/components/Cta/Cta';
import i18n from '@/settings/i18n';
import useLoading from '@/lib/use-loading';
import { logger } from '@/lib/logger';
import { useCustomerContext } from '@/lib/customer/CustomerContext';

const t = i18n.init();

export default function SubscribeNewsletter(props: { className?: string }) {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const { updateEmailSubscription } = useCustomerContext();

  if (!recaptchaSiteKey) logger.warn('Recaptcha site key is missing');

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const form = useForm({
    resolver: yupResolver(
      yup
        .object()
        .shape({
          email: yup
            .string()
            .trim()
            .email(t`Oops, this is not a valid email. Let's try again`)
            .required(t`Hey, you forgot to set your email.`),
        })
        .required(),
    ),
  });
  const [success, setSuccess] = useState(false);
  const [loading, subscribe] = useLoading(async (email: string) => {
    try {
      const token = await recaptchaRef.current?.executeAsync();

      if (!token) throw new Error('Recaptcha token is missing');

      await updateEmailSubscription({
        email,
        status: CustomerSubscriptionStatus.OPTED_IN,
        source: CustomerSubscriptionSource.FOOTER,
      });

      setSuccess(true);

      form.reset();
    } catch (error) {
      setSuccess(false);
    }
  });

  const error = form.formState.errors.email?.message;

  return (
    <form onSubmit={form.handleSubmit(({ email }) => subscribe(email))}>
      <div
        className={cn(
          'grid w-full text-center text-xs sm:max-w-[320px]',
          props.className,
        )}
      >
        <div>{t`Unlock exclusive discounts, new arrivals, and daily deals.`}</div>
        <input
          placeholder={t`Enter your email`}
          className="mt-1 rounded rounded-b-[0px] p-3 text-sm text-gray-700 outline-none"
          aria-label="Enter email to subscribe"
          type="email"
          {...form.register('email')}
        />
        <CtaButton
          type="submit"
          disabled={success}
          variant="teal"
          loading={loading}
          className="!my-0 !rounded-t-[0px] disabled:opacity-50"
        >
          {success ? t`All good! You are subscribed` : t`Subscribe`}
        </CtaButton>
        {error && <div className="text-red">{error}</div>}
        <div className="mt-1">
          {t`By subscribing, you accept our`}{' '}
          <Link href="/policies/privacy-policy">
            <a className="underline">{t`Privacy Policy`}</a>
          </Link>
        </div>
        {recaptchaSiteKey && (
          <>
            <div className="mt-2">
              This site is protected by reCAPTCHA and the Google
              <a href="https://policies.google.com/privacy">
                {' '}
                Privacy Policy
              </a>{' '}
              and
              <a href="https://policies.google.com/terms">
                {' '}
                Terms of Service
              </a>{' '}
              apply.
            </div>
            <ReCAPTCHA
              size="invisible"
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
            />
          </>
        )}
      </div>
    </form>
  );
}
